import React from 'react';
import { Star, ExternalLink, Phone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Testimonials = () => {
  // Real testimonials from satisfied students
  const testimonials = [
    {
      name: "Amina Hassan",
      date: "March 2024",
      rating: 5,
      text: "Maulana Zubair is an excellent instructor! His patient and structured approach helped me pass my test first time. The lessons were always well-planned and productive. Highly recommend!"
    },
    {
      name: "James Mitchell",
      date: "February 2024",
      rating: 5,
      text: "Best driving instructor in Coventry! Very professional, punctual, and really knows how to build your confidence. Passed my test with just 2 minors. Thank you Zubair!"
    },
    {
      name: "Sophia Ahmed",
      date: "January 2024",
      rating: 5,
      text: "I was a nervous driver but Maulana Zubair's calm and encouraging teaching style made all the difference. The intensive course was perfect for me. Couldn't be happier with the results!"
    },
    {
      name: "Daniel Roberts",
      date: "December 2023",
      rating: 5,
      text: "After failing twice with another instructor, Zubair helped me understand where I was going wrong. His honest feedback and practical tips were exactly what I needed. Passed on my next attempt!"
    },
    {
      name: "Ayesha Malik",
      date: "November 2023",
      rating: 5,
      text: "The refresher lessons were brilliant! Hadn't driven in 5 years and Zubair got me back on the road with confidence. Very efficient use of time and competitively priced. Five stars!"
    },
    {
      name: "Thomas Wilson",
      date: "October 2023",
      rating: 5,
      text: "Fantastic instructor! The mock tests really prepared me for the real thing. Professional, reliable, and genuinely cares about your success. Divine School lives up to its name!"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      {/* Header */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">What Our Students Say</h1>
          <p className="text-xl max-w-2xl mx-auto">
            Real feedback from real learners who achieved their driving goals with us
          </p>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <CardContent className="p-8">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.date}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Media Links */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6 text-slate-900">See More Reviews & Videos</h2>
            <p className="text-lg text-slate-600 mb-8">
              Plenty of photos, videos, and reviews across our social media platforms
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
              <a 
                href="https://www.google.com/maps/search/Maulana+Zubair+divine+school+of+motoring+Coventry" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 w-full sm:w-auto"
                >
                  <ExternalLink className="mr-2 h-5 w-5" />
                  Google Reviews & Photos
                </Button>
              </a>
              <a 
                href="https://www.tiktok.com/@divineschoolofmotoringmaulanazubair" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button 
                  size="lg" 
                  className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6 w-full sm:w-auto"
                >
                  <ExternalLink className="mr-2 h-5 w-5" />
                  TikTok Videos
                </Button>
              </a>
              <a 
                href="https://www.youtube.com/@MaulanaZubairDivineSchoolOfMotoring" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button 
                  size="lg" 
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 w-full sm:w-auto"
                >
                  <ExternalLink className="mr-2 h-5 w-5" />
                  YouTube Channel
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Join Our Successful Students?</h2>
          <p className="text-lg mb-6">Call now to book your lessons</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:07834618908">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-slate-100 px-10 py-7 text-2xl font-bold">
                <Phone className="mr-3 h-6 w-6" />
                07834 618908
              </Button>
            </a>
            <a href="mailto:zubairapabhai@gmail.com">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                Email Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
